import React from 'react';
import { Button, Chip, FormControlLabel, Switch } from '@mui/material';
import Dayjs from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { FilterBar, useFilterBarStyles } from '../ui';
import type { IFilterState } from '../../lib/mockupLogsDashboard/types';
import { clampDateToRange, clampFilterRange, dateAdd, default7dFilter, prevPeriod } from '../../lib/mockupLogsDashboard/utils';
import { dateParam } from '../../lib/orderFilters';

const PRESETS = [
  { key: '1d', label: '1D' },
  { key: '7d', label: '7D' },
  { key: '30d', label: '30D' },
  { key: '90d', label: '90D' },
  { key: 'ytd', label: 'YTD' },
  { key: '1y', label: '1Y' },
] as const;

interface Props {
  minDate: string;
  maxDate: string;
  state: IFilterState;
  onChange: (next: IFilterState) => void;
  disabled?: boolean;
}

/** Date range with quick presets and an optional comparison period (defaults to the period just before). */
const MockupLogsFilterBar: React.FC<Props> = ({ minDate, maxDate, state, onChange, disabled = false }) => {
  const classes = useFilterBarStyles();

  const applyPreset = (p: string) => {
    let next: IFilterState = { ...state, preset: p };
    const maxD = maxDate;
    if (p === '1d') next = { ...next, start: maxD, end: maxD };
    else if (p === '7d') next = { ...next, ...default7dFilter(maxD) };
    else if (p === '30d') next = { ...next, start: dateAdd(maxD, -29), end: maxD };
    else if (p === '90d') next = { ...next, start: dateAdd(maxD, -89), end: maxD };
    else if (p === 'ytd') next = { ...next, start: maxD.slice(0, 4) + '-01-01', end: maxD };
    else if (p === '1y') next = { ...next, start: dateAdd(maxD, -364), end: maxD };
    const clamped = clampFilterRange(next.start, next.end, minDate, maxD);
    next = { ...next, start: clamped.start, end: clamped.end };
    if (next.compare) {
      const pp = prevPeriod(next.start, next.end, minDate);
      next = { ...next, cstart: pp.cstart, cend: pp.cend };
    }
    onChange(next);
  };

  const setCompare = (on: boolean) => {
    let next = { ...state, compare: on };
    if (on && (!next.cstart || !next.cend)) {
      const p = prevPeriod(next.start, next.end, minDate);
      next = { ...next, cstart: p.cstart, cend: p.cend };
    }
    onChange(next);
  };

  const setRange = (start: string, end: string) => {
    const clamped = clampFilterRange(start, end, minDate, maxDate);
    if (state.compare) {
      const p = prevPeriod(clamped.start, clamped.end, minDate);
      onChange({ ...state, start: clamped.start, end: clamped.end, preset: 'custom', cstart: p.cstart, cend: p.cend });
    } else {
      onChange({ ...state, start: clamped.start, end: clamped.end, preset: 'custom' });
    }
  };

  const onDate = (apply: (iso: string) => void) => (value: Dayjs.Dayjs | null) => {
    const iso = dateParam(value);
    if (iso) apply(iso);
  };

  const pickerProps = { format: 'YYYY-MM-DD', minDate: Dayjs(minDate), maxDate: Dayjs(maxDate), disabled, slotProps: { textField: { size: 'small' as const } } };

  return (
    <FilterBar label="Date range">
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker className={classes.date} label="From" value={Dayjs(state.start)} onChange={onDate((iso) => setRange(iso, state.end))} {...pickerProps} />
        <DatePicker className={classes.date} label="To" value={Dayjs(state.end)} onChange={onDate((iso) => setRange(state.start, iso))} {...pickerProps} />
        <div role="group" aria-label="Quick ranges" style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {PRESETS.map((p) => (
            <Chip
              key={p.key}
              label={p.label}
              size="small"
              clickable
              disabled={disabled}
              aria-pressed={state.preset === p.key}
              color={state.preset === p.key ? 'primary' : 'default'}
              variant={state.preset === p.key ? 'filled' : 'outlined'}
              onClick={() => applyPreset(p.key)}
            />
          ))}
        </div>
        <FormControlLabel
          sx={{ m: 0 }}
          control={<Switch size="small" checked={state.compare} disabled={disabled} onChange={(event) => setCompare(event.target.checked)} />}
          label="Compare with previous period"
        />
        {state.compare && (
          <>
            <DatePicker
              className={classes.date}
              label="Compare from"
              value={Dayjs(state.cstart)}
              onChange={onDate((raw) => {
                const cstart = clampDateToRange(raw, minDate, maxDate);
                onChange({ ...state, cstart, cend: state.cend < cstart ? cstart : state.cend });
              })}
              {...pickerProps}
            />
            <DatePicker
              className={classes.date}
              label="Compare to"
              value={Dayjs(state.cend)}
              onChange={onDate((raw) => {
                const cend = clampDateToRange(raw, minDate, maxDate);
                onChange({ ...state, cend: cend < state.cstart ? state.cstart : cend });
              })}
              {...pickerProps}
            />
          </>
        )}
      </LocalizationProvider>
      <Button
        className={classes.clear}
        variant="text"
        size="small"
        disabled={disabled}
        onClick={() => onChange({ ...default7dFilter(maxDate), compare: false, cstart: '', cend: '' })}
      >
        Reset to 7 days
      </Button>
    </FilterBar>
  );
};

export default MockupLogsFilterBar;
